import { create } from 'zustand';
import { getDeviceId } from '@/lib/deviceId';

// EV-424 — on login, associate this device's ANONYMOUS check-ins (stored against
// the device cookie while logged out) to the now-authenticated account.
async function claimAnonCheckins(token: string | null): Promise<void> {
  if (!token) return;
  try {
    const base = (import.meta as any).env?.VITE_API_URL || localStorage.getItem('hl_api_base') || 'https://core.honestlee.app';
    await fetch(`${base}/api/me/checkins/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ device_id: getDeviceId() }),
    });
  } catch { /* best-effort */ }
}

// ─── Inline types (will be refactored to shared imports later) ───

interface User {
  id: number;
  email: string | null;
  name: string | null;
  avatar_url: string | null;
  provider: 'google' | 'line' | 'email' | 'guest';
  role: 'user' | 'agent' | 'admin';
  region: string | null;
  preferred_language: string | null;
  isCurator?: boolean;          // EV-588 — may add/curate places
  created_at: string;
}

interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy: number | null;   // metres
  timestamp: number;         // Date.now()
}

// ─── localStorage helpers ───

const TOKEN_KEY = 'hl_auth_token';
const USER_KEY = 'hl_user';

// EV-358 (Patrick 2026-06-08): a token that's present-but-EXPIRED must NOT count
// as logged-in (it was hydrating isAuthenticated=true forever). Decode the JWT
// exp client-side; treat malformed/expired as invalid.
function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  try {
    const part = token.split('.')[1];
    if (!part) return false;
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    if (json && typeof json.exp === 'number') return json.exp * 1000 > Date.now();
    return true; // no exp claim → can't prove expired, accept
  } catch { return false; }
}

// EV-358 — every auth key the legacy + v2 login paths have ever written. Logout /
// invalid-token must wipe ALL of them or stale keys keep the user "logged in".
const ALL_AUTH_KEYS = [
  'hl_auth_token', 'hl_user', 'venue_app_token', 'venueapp_token', 'token', 'authToken',
  'user_id', 'userId', 'user_name', 'userName', 'user_email', 'userEmail',
  'user_phone', 'userPhone', 'user_role', 'userRole', 'user_login_method', 'user_loginmethod',
  'loginMethod', 'user_profile_image', 'user_qr_token', 'qr_token',
];
function wipeAllAuth(): void {
  try { ALL_AUTH_KEYS.forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ }
}

function loadToken(): string | null {
  try {
    // EV-358 — accept ANY of the known auth-token keys (the legacy login path
    // writes venue_app_token/token; the v2 path writes hl_auth_token). Whichever
    // exists and is non-expired counts as logged-in. This avoids signing out a
    // legacy-logged-in user, while still clearing a stale/expired token.
    const t = localStorage.getItem(TOKEN_KEY)
      || localStorage.getItem('venue_app_token')
      || localStorage.getItem('token');
    if (t && !isTokenValid(t)) { wipeAllAuth(); return null; }  // stale → clear everything
    return t || null;
  } catch {
    return null;
  }
}

function loadUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * CON-1499 / AN-1498 — publish a cross-property identity HINT to a
 * `.honestlee.app` cookie so the marketing sites (honestlee.app,
 * web.honestlee.app) can greet the person by name and prefill the contact form.
 *
 * 🔴 NOT AUTHENTICATION. No token, grants nothing, the server never trusts it.
 * It exists only because honestlee.app physically cannot read this origin's
 * localStorage — a shared-domain cookie is the one legitimate channel between
 * our properties. Cleared on logout.
 */
function publishIdentityHint(user: User | null, token?: string | null): void {
  try {
    const host = (window.location.hostname || '').toLowerCase();
    if (host !== 'honestlee.app' && !host.endsWith('.honestlee.app')) return;
    const base = '; path=/; SameSite=Lax; domain=.honestlee.app';
    if (!user) {
      document.cookie = `hl_who=${base}; max-age=0`;
      document.cookie = `hl_sess=${base}; max-age=0`;
      return;
    }
    const u = user as any;
    // CON-1522 — carry the avatar so the marketing site can show the face from
    // their Google / Zalo / Line login. Patrick 2026-08-03: "the avatar of the
    // Google account or Zalo account, if they authenticated, would be very
    // impressive. Just a little avatar. We saved those already."
    // Capped at 300 chars: this rides in a cookie, and a runaway data: URI
    // would blow the 4KB budget and silently drop the whole hint.
    const avatar = [u.avatar_url, u.profile_image, u.zalo_picture, u.line_picture, u.picture]
      .find((x: unknown) => typeof x === 'string' && /^https?:\/\//.test(x) && x.length <= 300);
    const payload = JSON.stringify({
      id:    u.id ? String(u.id).slice(0, 64) : undefined,
      name:  u.name ? String(u.name).slice(0, 80) : undefined,
      email: u.email ? String(u.email).slice(0, 120) : undefined,
      phone: u.phone ? String(u.phone).slice(0, 32) : undefined,
      avatar: avatar || undefined,
    });
    document.cookie = `hl_who=${encodeURIComponent(payload)}${base}; max-age=${60 * 60 * 24 * 180}`;

    // 🔴 CON-1535c — PUBLISH THE JWT FROM HERE TOO, not only from the admin app.
    //
    // Patrick 2026-08-03: "this has nothing to do with opening admin. When I'm
    // logged in with an admin account — that's the account class, the user
    // class admin, which I am — the front end should see all those filters.
    // It's admin-gated in that way."
    //
    // He is right and my earlier fix was aimed at the wrong place. Until now
    // ONLY apps/frontend-admin published hl_sess, so the marketing site could
    // learn someone's NAME from any surface but their ROLE from exactly one —
    // and a PWA-only admin was invisible as an admin forever. Publishing the
    // same signed token here means the role travels with the person, not with
    // whichever app they happened to open last.
    //
    // Safe: the cookie is not the authority. The backend verifies the
    // signature and expiry on every request, so a forged or stale value is
    // rejected server-side. Cleared on logout with the rest of the hint.
    if (token) {
      document.cookie = `hl_sess=${encodeURIComponent(token)}${base}; max-age=${60 * 60 * 12}`;
    }
  } catch { /* cookies blocked — prefill simply won't happen */ }
}

function persistAuth(user: User | null, token: string | null): void {
  try {
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    publishIdentityHint(user, token);
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// ─── Store ───

interface UserStore {
  // Auth state
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User, token: string) => void;
  updateUser: (partial: Partial<User>) => void;
  clearUser: () => void; // logout

  // GPS location
  userLocation: UserLocation | null;
  setUserLocation: (loc: UserLocation) => void;
  clearUserLocation: () => void;
}

const _initToken = loadToken();   // EV-358 — also wipes if expired

/**
 * 🔴 CON-1499 — republish the identity hint ON BOOT, not only on login.
 *
 * Patrick 2026-08-03: "It took me to the PWA where I'm already signed in.
 * Clicking back doesn't tell me that I'm signed in."
 *
 * publishIdentityHint() lived only inside persistAuth(), which runs on
 * setUser/updateUser/clearUser. Someone who logged in BEFORE this shipped —
 * i.e. essentially every existing user — rehydrates from localStorage on boot
 * and never calls any of them, so the `.honestlee.app` cookie was never
 * written and the marketing site had no way to know them. Publishing here
 * covers the already-signed-in case, which is the overwhelmingly common one.
 *
 * Cheap and idempotent: one cookie write per page load, same value each time.
 */
try {
  const _bootUser = _initToken ? loadUser() : null;
  publishIdentityHint(_bootUser, _initToken);
} catch { /* cookies blocked — prefill simply won't happen */ }

export const useUserStore = create<UserStore>((set, get) => ({
  user: _initToken ? loadUser() : null,
  token: _initToken,
  isAuthenticated: _initToken != null,

  setUser: (user, token) => {
    persistAuth(user, token);
    set({ user, token, isAuthenticated: true });
    claimAnonCheckins(token);   // EV-424 — claim this device's anon check-ins
  },

  updateUser: (partial) => {
    const current = get().user;
    if (!current) return;
    const updated = { ...current, ...partial };
    persistAuth(updated, get().token);
    set({ user: updated });
  },

  clearUser: () => {
    persistAuth(null, null);
    wipeAllAuth();   // EV-358 — also nuke every legacy auth key, not just hl_*
    set({ user: null, token: null, isAuthenticated: false });
  },

  userLocation: null,
  setUserLocation: (userLocation) => set({ userLocation }),
  clearUserLocation: () => set({ userLocation: null }),
}));
